/**
 * "Convidar amigos" (docs/spec/friends.md): da mesa, escolher amigos e mandar o convite, que chega
 * na conversa com cada um como um cartão com "Entrar". Quem já está na mesa não precisa de convite.
 */
import { useState } from 'react';
import { useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import { Button, CheckBox, Dialog } from '../ui/controls.js';
import { SearchBox } from '../ui/SearchBox.js';
import { toast } from '../ui/Toaster.js';
import { Avatar } from './Avatar.js';
import { friendStatusText, useNow } from './FriendChatWindow.js';
import { fold, presenceDot } from './friendsModel.js';

export function InviteFriendsDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const contacts = useApp((s) => s.contacts);
  const roomName = useApp((s) => s.room?.name ?? '');
  const users = useApp((s) => s.users);
  const inviteFriends = useApp((s) => s.inviteFriends);
  const typing = useApp((s) => s.dmTyping);
  const now = useNow();
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const here = new Set(users.map((u) => u.id));
  const q = fold(query.trim());
  // online primeiro, depois o nome
  const friends = [...(contacts?.friends ?? []), ...(contacts?.hidden ?? [])]
    .filter((c) => !q || fold(c.user.displayName).includes(q))
    .sort((a, b) => Number(b.presence.online) - Number(a.presence.online) || a.user.displayName.localeCompare(b.user.displayName));

  const toggle = (id: string, on: boolean) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const send = async () => {
    setBusy(true);
    const n = await inviteFriends([...chosen]);
    setBusy(false);
    if (!n) return;
    toast(t('friends.invite.done', { n }), 'success');
    onClose();
  };

  return (
    <Dialog
      title={t('friends.invite.title', { room: roomName })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" busy={busy} disabled={!chosen.size} onClick={() => void send()}>
            {chosen.size ? t('friends.invite.sendN', { n: chosen.size }) : t('friends.invite.send')}
          </Button>
        </>
      }
    >
      {friends.length || q ? (
        <>
          <SearchBox className="rd-friends__search rd-friends__search--dialog" value={query} onChange={setQuery} placeholder={t('friends.search')} />
          <ul className="rd-inviteList" aria-label={t('friends.title')}>
            {friends.map((c) => {
              const inRoom = here.has(c.user.id);
              const status = friendStatusText(c, !!typing[c.user.id], now, t);
              return (
                <li key={c.user.id} className={inRoom ? 'is-here' : ''}>
                  <CheckBox checked={chosen.has(c.user.id)} onChange={(on) => !inRoom && toggle(c.user.id, on)}>
                    <span className="rd-inviteList__who">
                      <Avatar name={c.user.displayName} color={c.user.color} userId={c.user.id} avatarVersion={c.user.avatarVersion} size={30} presence={presenceDot(c.presence)} />
                      <span className="rd-friends__rowText">
                        <span className="rd-friends__name">{c.user.displayName}</span>
                        <span className={`rd-friends__status is-${status.tone}`}>{inRoom ? t('friends.invite.alreadyHere') : status.text}</span>
                      </span>
                    </span>
                  </CheckBox>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <p className="rd-friends__hint">{t('friends.invite.noFriends')}</p>
      )}
    </Dialog>
  );
}
